import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import keyMap from './keyMap';

const ProfileModal = ({
  open,
  onClose,
  selectedEntity,
  userRole,
  newNote,
  setNewNote,
  handleNoteAdd,
  handleDownloadProfile,
  handleRiskRatingChange,
  handleRiskIndicatorToggle,
  theme = 'dark',
}) => {
  if (!selectedEntity) return null;

  const formatLabel = (key) => {
    if (keyMap[key]) return keyMap[key];
    for (const label of Object.keys(keyMap)) {
      if (keyMap[label].some((mappedKey) => mappedKey.includes('.') && mappedKey.split('.')[0] === key)) {
        return key;
      }
    }
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/Of/g, "of")
      .replace(/No/g, "No")
      .trim();
  };

  const renderFinalizedData = (data, prefix = "", indent = 0) => {
    if (!data || typeof data !== "object") {
      return (
        <Typography variant="body2" sx={{ ml: indent * 2, color: "var(--text-secondary)" }}>
          {data === null || data === undefined || data === '' ? "-" : data.toString()}
        </Typography>
      );
    }

    return Object.entries(data).map(([key, value]) => {
      const label = formatLabel(key);
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        return (
          <Box key={fullKey} sx={{ mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ ml: indent * 2, fontWeight: 600, color: "var(--text-color)" }}
            >
              {label}
            </Typography>
            {renderFinalizedData(value, fullKey, indent + 1)}
          </Box>
        );
      }

      return (
        <Box key={fullKey} sx={{ mb: 1, display: "flex", alignItems: "center" }}>
          <Typography
            variant="body2"
            sx={{ ml: indent * 2, fontWeight: 500, color: "var(--text-color)", minWidth: "200px" }}
          >
            {label}:
          </Typography>
          <Typography variant="body2" sx={{ color: "var(--text-secondary)" }}>
            {value === null || value === undefined || value === ''
              ? "-"
              : typeof value === "boolean"
              ? value
                ? "Yes"
                : "No"
              : value === 'NIC'
              ? 'Not Provided'
              : value.toString()}
          </Typography>
        </Box>
      );
    });
  };

  // Temporary debugging for unmapped keys
  const data = selectedEntity.finalized_data || selectedEntity.extracted_data || selectedEntity.initial_data || {};
  const usedKeys = Object.values(keyMap).flat().map((key) => key.includes('.') ? key.split('.')[0] : key);
  const metadataKeys = ['user_id', 'pdf_path', 'is_extracted', 'is_confirmed', 'reference_id', 'mongo_id', 'serialNo', 'import_export', 'riskRating'];
  const unmappedData = {};
  Object.keys(data).forEach((key) => {
    if (!usedKeys.includes(key) && !metadataKeys.includes(key) && typeof data[key] !== 'object') {
      unmappedData[key] = { value: data[key] };
    }
  });
  if (Object.keys(unmappedData).length > 0) {
    console.log('Unmapped keys in ProfileModal:', unmappedData);
  }

  const handleDownload = () => {
    try {
      handleDownloadProfile();
      toast.success('Profile downloaded successfully!', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === 'dark' ? '#d4edda' : '#d1fae5',
          color: '#155724',
          borderRadius: '8px',
          fontSize: '14px',
        },
      });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download profile: ' + (error.message || 'Unknown error'), {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === 'dark' ? '#f8d7da' : '#fee2e2',
          color: '#721c24',
          borderRadius: '8px',
          fontSize: '14px',
        },
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: `${theme}-theme`,
        sx: {
          borderRadius: "var(--border-radius-md)",
          boxShadow: "var(--box-shadow)",
          bgcolor: "var(--card-background)",
          backdropFilter: "var(--backdrop-blur)",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "var(--input-background)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: "16px",
          px: "24px",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-color)" }}>
          {selectedEntity.applicant_name || "Profile Details"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            className="btn-contained"
            sx={{
              textTransform: "none",
              fontSize: "14px",
              px: "16px",
            }}
          >
            Download Profile
          </Button>
          <IconButton
            onClick={onClose}
            aria-label="Close modal"
            sx={{ color: "var(--text-secondary)" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: "24px" }}>
        <Box sx={{ mb: "24px" }}>
          <Typography
            variant="h6"
            sx={{ mb: "16px", fontWeight: 600, color: "var(--text-color)" }}
          >
            Basic Information
          </Typography>
          {selectedEntity.finalized_data ? (
            renderFinalizedData(selectedEntity.finalized_data)
          ) : (
            <Typography variant="body2" sx={{ color: "var(--text-secondary)" }}>
              No finalized data available.
            </Typography>
          )}
        </Box>
        <Divider sx={{ my: "24px", borderColor: "var(--border-color)" }} />
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;